import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, KeyRound, Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { homeForGroups } from '../../auth/routing';
import { claimRestorePoint } from '../../utils/sessionRestore';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import loginImage from '../../assets/login image bhima.png';
import bhimaLogo from '../../assets/bhima-logo.svg';
import styles from '../../styles/authShell.module.css';

export default function Login() {
  const { login, logout, authNotice } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetNotice = searchParams.get('reset') === '1';
  const timeoutNotice = authNotice || (searchParams.get('reason') === 'session_expired' ? 'Session timed out. Please sign in again.' : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const session = await login(identifier, pin);
      const home = homeForGroups(session.groups);
      if (!home) {
        await logout();
        setError('This app is for store staff and delivery partners. Please use the BHIMA customer app.');
        return;
      }
      // Same user coming back after an inactivity sign-out resumes the screen
      // they were on, with what they had typed.
      navigate(claimRestorePoint(session.sub) || home, { replace: true });
    } catch (err) {
      if (err.code === 'UserNotConfirmedException') {
        navigate('/signup/confirm', {
          state: identifier.startsWith('+') ? { initialPhone: identifier } : undefined,
        });
        return;
      }
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.panel}>
        <img src={loginImage} alt="" className={styles.panelImage} />
      </div>

      <div className={styles.formSide}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <img src={bhimaLogo} alt="Bhima Store" className={styles.mobileLogo} />
          <p className={styles.eyebrow}>Store staff sign in</p>
          <h2 className={styles.formTitle}>Welcome back</h2>
          <p className={styles.formSubtitle}>Sign in to open today's order queue.</p>

          {resetNotice && <AuthMessage tone="success">Your PIN was reset. Sign in with your new PIN.</AuthMessage>}
          {timeoutNotice && <AuthMessage>{timeoutNotice}</AuthMessage>}
          <AuthMessage>{error}</AuthMessage>

          <label className={styles.field}>
            <span>Phone or email</span>
            <div className={styles.inputWrap}>
              <Phone size={15} />
              <input
                type="text"
                required
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="+91XXXXXXXXXX or you@example.com"
              />
            </div>
          </label>

          <label className={styles.field}>
            <span>4-digit PIN</span>
            <div className={styles.inputWrap}>
              <KeyRound size={15} />
              <input
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                required
                autoComplete="current-password"
                className={styles.pinInput}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
              />
            </div>
          </label>

          <div className={styles.rowBetween}>
            <span />
            <Link to="/forgot-pin" className={styles.linkBtn}>Forgot PIN?</Link>
          </div>

          <Button
            type="submit"
            size="lg"
            icon={ArrowRight}
            iconPosition="right"
            disabled={loading || pin.length !== 4 || !identifier}
            className={styles.submit}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>

          {/* <p className={styles.helper}>
            New here? <Link to="/signup" className={styles.linkBtn}>Create an account</Link>
          </p> */}
        </form>
      </div>
    </div>
  );
}
